import { BUSINESS_TIME_ZONE } from './businessDays.js';
import { instantForWallClock, isValidTimeZone, timeZoneShortLabel, wallClockIn } from './localTime.js';
import { resolveTimeZoneFromAddress } from './regionTimeZone.js';
/** The partner's business hours: 08:00 to 17:00 site time, Monday (1) to Friday (5). */
export const PARTNER_SERVICE_WINDOW = { startHour: 8, endHour: 17, days: [1, 2, 3, 4, 5] };
/** Every charger at the site down: 07:00 to 21:00 site time, every day. */
export const SITE_DOWN_WINDOW = { startHour: 7, endHour: 21, days: [0, 1, 2, 3, 4, 5, 6] };
export const SERVICE_ANSWER_MS = 4 * 3_600_000;
export const SITE_DOWN_ANSWER_MS = 3_600_000;
/** The zone a site's service clock reads: its own recorded zone, else its state or province, else ET (never a guess
 * from the server's clock). */
export function serviceTimeZone(site) {
    if (site?.timezone && isValidTimeZone(site.timezone))
        return site.timezone;
    const fromAddress = site?.address ? resolveTimeZoneFromAddress(site.address).timezone : null;
    return fromAddress && isValidTimeZone(fromAddress) ? fromAddress : BUSINESS_TIME_ZONE;
}
const nextDay = (d) => {
    const t = new Date(Date.UTC(d.year, d.month - 1, d.dayOfMonth + 1));
    return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, dayOfMonth: t.getUTCDate() };
};
/** `start` plus `ms` of time inside the window, in the site's zone: the clock runs only inside it and waits for the
 * next opening outside it (so a call at 22:00 starts counting at the window's next start). */
export function addWindowTime(start, ms, window, timeZone) {
    const zone = isValidTimeZone(timeZone) ? timeZone : BUSINESS_TIME_ZONE;
    let cursor = start.getTime(), remaining = Math.max(0, ms);
    for (let guard = 0; guard < 1000; guard++) {
        const w = wallClockIn(new Date(cursor), zone), today = { year: w.year, month: w.month, dayOfMonth: w.dayOfMonth };
        if (window.days.includes(w.day)) {
            const open = instantForWallClock({ ...today, hour: window.startHour }, zone).getTime();
            const close = instantForWallClock({ ...today, hour: window.endHour }, zone).getTime();
            const from = Math.max(cursor, open);
            if (from < close) {
                if (remaining <= close - from)
                    return new Date(from + remaining);
                remaining -= close - from;
            }
        }
        cursor = instantForWallClock({ ...nextDay(today), hour: 0 }, zone).getTime();
    }
    throw new Error('addWindowTime: no window found');
}
/** When the partner's answer falls due: 4 business hours (08:00 to 17:00, Monday to Friday), or 1 hour (07:00 to 21:00,
 * every day) when every charger at the site is down. */
export function serviceAnswerDueAt(from, o) {
    return o.siteDown ? addWindowTime(from, SITE_DOWN_ANSWER_MS, SITE_DOWN_WINDOW, o.timeZone)
        : addWindowTime(from, SERVICE_ANSWER_MS, PARTNER_SERVICE_WINDOW, o.timeZone);
}
/** "Tue Oct 21, 11:00 ET": a due time as the site reads it. */
export function serviceTimeLabel(at, timeZone) {
    const zone = isValidTimeZone(timeZone) ? timeZone : BUSINESS_TIME_ZONE;
    const day = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric' }).format(at);
    const time = new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);
    return `${day}, ${time} ${timeZoneShortLabel(zone, 'en-US', at)}`;
}
export const SERVICE_REQUEST_STATUSES = ['open', 'accepted', 'fixed_pending', 'with_ops', 'warranty', 'closed'];
/** The status in the partner's words. */
export const SERVICE_STATUS_LABEL = {
    open: 'Waiting for an answer', accepted: 'Visit planned', fixed_pending: 'Fixed: EVI is checking it online', with_ops: 'With EVI ops',
    warranty: 'Warranty claim', closed: 'Closed',
};
/** The four answers the partner taps (ruling H). */
export const SERVICE_ANSWERS = ['handle', 'fixed', 'warranty', 'decline'];
export const SERVICE_ANSWER_LABEL = {
    handle: "I'll handle it", fixed: 'Fixed', warranty: 'Warranty: the unit is faulty', decline: "I can't take it",
};
export const WARRANTY_STEPS = ['claim_started', 'sent_to_autel', 'approved', 'replacement_shipped', 'replacement_delivered', 'old_unit_returned', 'closed'];
/** Each warranty step in plain words, as the partner reads it. */
export const WARRANTY_STEP_LABEL = {
    claim_started: 'Claim started', sent_to_autel: 'Sent to Autel', approved: 'Approved by Autel', replacement_shipped: 'Replacement shipped',
    replacement_delivered: 'Replacement delivered', old_unit_returned: 'Old unit returned', closed: 'Closed',
};
/** Said on the case and to the partner (Warren, 21 October 2026, answer 7). */
export const WARRANTY_SAME_ID_LINE = 'Autel pre-programs the replacement with the same charger ID before it ships, so the QR labels keep working.';
//# sourceMappingURL=serviceCalls.js.map